import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ArrowRight,
  Sparkles,
  Users,
  Zap,
  Target,
} from "lucide-react";
import "./SecondaryFeature.css";

/**
 * Custom hook to detect when an element enters the viewport
 * Replaces 'useInView' from framer-motion
 */
const useInView = (options) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    }, options);

    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [options]);

  return [ref, isInView];
};

const AttractiveFeatureCard = ({
  title,
  description,
  badge,
  isHighlighted = false,
  icon: Icon,
}) => {
  if (isHighlighted) {
    return (
      <div className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/60 border border-slate-100 overflow-hidden flex flex-col group transition-all duration-500 hover:shadow-violet-100/50">
        <div className="p-8 md:p-12 flex-grow">
          <h3 className="text-3xl md:text-2xl font-medium text-slate-90 leading-[1.1] mb-6 tracking-tight">
            {title}
          </h3>

          <p className="text-lg text-slate-500 leading-relaxed max-w-xl">
            {description}
          </p>
        </div>

        <button
          type="button"
          className="p-6 md:p-5 bg-slate-900 text-white flex items-center justify-between group-hover:bg-violet-700 transition-colors duration-500 cursor-pointer w-full border-0"
          onClick={() => window.open('https://app.youform.com/forms/qw5x4urb', '_blank')}
        >
          <div>
              <p className="text-base font-medium">Join waitlists</p>
          </div>
          <div className="w-10 h-10 bg-white text-slate-900 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <ArrowRight size={18} />
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-slate-200/40 border border-slate-100 flex flex-col gap-6 items-start hover:-translate-y-1 hover:border-indigo-100 transition-all duration-300 group">
      <div className="p-4 rounded-2xl group-hover:bg-violet-50 transition-colors">
        <Icon size={24} className="text-violet-600" />
      </div>
      <div>
        <h3 className="text-xl font-medium text-slate-800 mb-3">{title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed font-medium">
          {description}
        </p>
      </div>
    </div>
  );
};

AttractiveFeatureCard.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  badge: PropTypes.string,
  isHighlighted: PropTypes.bool,
  icon: PropTypes.elementType,
};

export default function App() {
  const [customerRef, customerInView] = useInView({ threshold: 0.1 });

  return (
    <div className="border-t min-h-screen py-20 overflow-x-hidden font-sans">
      <div className="max-w-7xl mx-auto px-4">
        {/* --- Features Section --- */}
        <div className="mb-40">
          <div className="text-center mb-20">
            <span className="feature-pill">Features</span>
            <h2 className="header-title">
              The first platform for vibe marketing
            </h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Create AI agents and automations tailored to run trendy social
              media marketing. Collaborate with teams or clients and plan
              campaigns together.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col">
              <AttractiveFeatureCard
                isHighlighted={true}
                title={
                  <>
                    One platform to plan, create, and publish social media at
                    scale.
                  </>
                }
                description={
                  <>
                    Built for growing brands, independent creators, and fast-moving agencies, this platform brings content creation, automation, and publishing into one unified workflow.
                    <br /><br />
                    Whether you're managing one account or many, the platform helps ensure content goes live as planned, stays aligned with brand guidelines, and scales smoothly as your social presence grows.
                  </>
                }
              />
            </div>

            <div className="flex flex-col gap-4 mt-0">
              <div className="bg-white p-6 rounded-2xl shadow-lg shadow-slate-200/40 border border-slate-100 flex items-start gap-5 hover:border-violet-100 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group">
                <div className="p-2 rounded-xl bg-violet-50 group-hover:bg-violet-100 transition-all duration-300 flex-shrink-0">
                  <Target  size={24} className="text-violet-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2 transition-colors duration-300">
                    For Brands
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed transition-colors duration-300">
                    Maintain a consistent, professional social media presence without relying on large teams or external agencies. Content stays on-brand, regularly published, and optimized for visibility—while daily effort stays minimal.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-lg shadow-slate-200/40 border border-slate-100 flex items-start gap-5 hover:border-indigo-100 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group">
                <div className="p-2 rounded-xl bg-violet-50 group-hover:bg-violet-100 transition-all duration-300 flex-shrink-0">
                  <Sparkles size={24} className="text-violet-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2 transition-colors duration-300">
                    For Creators
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed transition-colors duration-300">
                   Simplify idea generation, content creation, and publishing in one place. Stay consistent without the pressure of daily planning, and focus more on growing your audience.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-lg shadow-slate-200/40 border border-slate-100 flex items-start gap-5 hover:border-indigo-100 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group">
                <div className="p-2 rounded-xl bg-violet-50 group-hover:bg-violet-100 transition-all duration-300 flex-shrink-0">
                  <Users size={24} className="text-violet-600 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2 transition-colors duration-300">
                    For Agencies
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed transition-colors duration-300">
                    Manage multiple clients, campaigns, and platforms from a single workspace. Reduce manual work, improve turnaround times, and scale operations without increasing team size.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Customer Section --- */}
        <div ref={customerRef} className="pt-20 border-t border-slate-200">
          <div className="grid lg:grid-cols-2 gap-0 items-start">
            {/* Left Content */}
            <div
              className={`transition-all duration-1000 transform lg:sticky lg:top-40 ${
                customerInView
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-10"
              }`}
            >
              <span className="feature-pill">Our Customer</span>
              <h2 className="header-title">
                Who’s gonna use <br /> adflyn?
              </h2>
              <p className="text-xl text-slate-500 leading-relaxed max-w-md">
                Whether you run a local store, a growing startup, or a creative
                agency — Adflyn helps you market smarter, faster, and more
                affordably.
              </p>
            </div>

            {/* Right Content */}
            <div
              className={`grid gap-6 transition-all duration-1000 delay-300 transform ${
                customerInView
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-10"
              }`}
            >
              <div className="p-8 bg-white rounded-3xl border border-slate-100 shadow-lg">
                <h4 className="text-xl font-medium text-slate-800 mb-3">
Startup Founders & Entrepreneurs Founders
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed">
              Adflyn turns your ideas into ready-to-post designs and campaigns while you focus on building your business.
                </p>
              </div>
              <div className="p-8 bg-white rounded-3xl border border-slate-100 shadow-lg">
                <h4 className="text-xl font-medium text-slate-800 mb-3">
                  Local Businesses & Creators From cafés to boutiques to personal brands
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Anyone trying to reach their audience online can use Adflyn to stay consistent, visible, and professional with minimal effort.
                </p>
              </div>

              {/* Extra Value Card */}
              <div className="bg-violet-600 p-8 rounded-3xl text-white shadow-xl relative overflow-hidden group">
                <Zap className="absolute top-0 right-0 p-4 opacity-50 group-hover:scale-110 transition-transform w-32 h-32" />
                <h4 className="text-xl font-semibold mb-2">Ready to grow?</h4>
                <p className="text-indigo-100 mb-6 font-medium">
                  Join 500+ brands using Adflyn today.
                </p>
                <button className="px-6 py-2 bg-white text-indigo-600 rounded-full font-medium text-sm hover:bg-indigo-50 transition-colors" onClick={() => window.open('https://app.youform.com/forms/qw5x4urb', '_blank')}>
                  Join
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
